import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureFlagValue, useFlagIsActivated } from 'hooks/featureFlag';

import { getFeatureFlagCreator } from '../../creators/featureFlag';

/**
 * Conditionally displays the children, only if the feature flag `flag` value
 * is one of the values in `activationValues`
 */
export default function FeatureFlaggedComponent(props) {
  const {
    children,
    flag,
    activationValues,
    eligibilityPreCheckFunction,
    extraProperties,
  } = props;
  const showComponent = useFlagIsActivated({
    flag,
    activationValues,
    eligibilityPreCheckFunction,
    extraProperties,
  });

  return showComponent && children ? children : null;
}

export function FeatureFlagCheck({ children, flag }) {
  const flagValue = useFeatureFlagValue(flag);

  return children(flagValue);
}

/**
 * None blocking feature flag prefetch wrapper (helps ensure your redux state flags cache is hot)
 *
 * Use this above a component in the component heirachy when you know that you'll be using these flags
 * and would like to fetch them upfront.
 */
export const PrefetchFlags = ({ children = null, flags }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    flags.map(flag => dispatch(getFeatureFlagCreator(flag)));
  }, [dispatch, flags]);

  return children;
};

/**
 * Block (show nothing) until a feature flag has a determined state.
 *
 * USE WITH CARE - this will slow the UI down because our current feature flag fetch strategy is not fast.
 * When using this component you should be planning to remove it shortly after release of a feature.
 */
export const WaitForFlag = ({ children, flag }) => {
  const dispatch = useDispatch();
  const flagState = useSelector(state => state.feature_flags[flag]);

  useEffect(() => {
    dispatch(getFeatureFlagCreator(flag));
  }, [dispatch, flag, flagState]);

  if (flagState?.value == null) {
    // block child render here;
    return null;
  }

  return children;
};
